import CompetitionTableRow from "./CompetitionTableRow";
import ModernTable from "../shared/ModernTable";
import SortButton from "../shared/SortButton";
import { CompetitionWithId } from "../../interfaces/ICompetitions";
import { filterAndSortCompetitions, SortDirection } from "../../utils/competitionHelpers";
import { hasVisibleFinalResults, hasVisibleRoundResults } from "../../utils/resultPublicationUtils";
import { useMemo, useState } from "react";
import { useNavigationHelper } from "../../hooks/useNavigationHelper";
import { usePublishedScheduleContestIds } from "../../hooks/usePublishedScheduleContestIds";

interface CompetitionsTableProps {
  competitions: CompetitionWithId[];
}

export default function CompetitionsTable({ competitions }: CompetitionsTableProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const { goToCategories, goToFinalResultsCategories, goToSchedule } = useNavigationHelper();
  const { contestIds: contestIdsWithPublishedSchedule, loading: scheduleAvailabilityLoading } =
    usePublishedScheduleContestIds(competitions);

  const filteredAndSortedCompetitions = useMemo(() => {
    return filterAndSortCompetitions(competitions, searchTerm, sortDirection);
  }, [competitions, searchTerm, sortDirection]);

  const handleSortToggle = () => {
    setSortDirection((prev: SortDirection) => (prev === "desc" ? "asc" : "desc"));
  };

  const handleRowClick = (competition: CompetitionWithId) => {
    const categories = competition.categories || [];
    const hasRounds = categories.some((category) => hasVisibleRoundResults(competition, category));

    if (hasRounds) {
      goToCategories(competition.id);
      return;
    }

    const hasFinals = categories.some((category) => hasVisibleFinalResults(category));

    if (hasFinals) {
      goToFinalResultsCategories(competition.id);
    }
  };

  const handleResultsClick = (competitionId: string) => {
    goToCategories(competitionId);
  };

  const handleFinalResultsClick = (competitionId: string) => {
    goToFinalResultsCategories(competitionId);
  };

  const handleScheduleClick = (competitionId: string) => {
    goToSchedule(competitionId);
  };

  const columns = [
    {
      id: "competition",
      label: "Turniej",
      align: "left" as const,
    },
  ];

  return (
    <ModernTable
      data={filteredAndSortedCompetitions}
      columns={columns}
      searchPlaceholder="Wyszukaj turniej..."
      onSearch={setSearchTerm}
      searchValue={searchTerm}
      emptyMessage={searchTerm ? "Nie znaleziono turniejów" : "Brak turniejów"}
      renderRow={(competition) => (
        <CompetitionTableRow
          competition={competition}
          showScheduleButton={
            !scheduleAvailabilityLoading && contestIdsWithPublishedSchedule.has(competition.id)
          }
          onRoundResultsClick={handleResultsClick}
          onFinalResultsClick={handleFinalResultsClick}
          onScheduleClick={handleScheduleClick}
        />
      )}
      onRowClick={handleRowClick}
      getItemKey={(competition) => competition.id}
      headerActions={
        <SortButton
          direction={sortDirection}
          onToggle={handleSortToggle}
          labelDesc="Najnowsze"
          labelAsc="Najstarsze"
          tooltipDesc="Sortuj: najnowsze na górze (kliknij, aby zmienić)"
          tooltipAsc="Sortuj: najstarsze na górze (kliknij, aby zmienić)"
        />
      }
    />
  );
}
